// src/components/WeighInModal/weighInProducts.js
import { mixDisplayName } from "../../utils/mixDisplayName";
import { byUploadedDesc } from "../../utils/sortByUploaded";

export const MAX_SEARCH_RESULTS = 8;

export function weighInDisplayName(product) {
    return mixDisplayName(product.product_name, product.product_type, product.created_at) || `Product #${product.id}`;
}

/**
 * The DYC color to show beside a product, or null when it has none.
 * Same rule as the Inventory table: only flower and pre-roll products carry a color.
 *
 * @param {object} product
 * @returns {string|null} Lowercase color name, or null.
 */
export function weighInColor(product) {
    const type = (product.product_type || "").toLowerCase();
    if (type !== "flower" && type !== "pre-roll") return null;
    const color = (product.color || "").toLowerCase();
    return color && color !== "no color" ? color : null;
}

/**
 * Split the Inventory page's products into the two weigh-in groups.
 * Both groups are ordered like the Inventory page's default sort: newest upload first.
 * Products without a name are dropped — the Inventory page does not list them.
 * Stock is the row's own quantity (the value a weigh-in replaces), so in the
 * license view a product held only at locations counts as out of stock.
 *
 * @param {Array} products - Dashboard `coas` state.
 * @returns {{ inStock: Array, outOfStock: Array }}
 */
export function partitionWeighInProducts(products) {
    const inStock = [];
    const outOfStock = [];
    for (const p of products || []) {
        if (!p || !p.product_name) continue;
        (parseFloat(p.quantity) > 0 ? inStock : outOfStock).push(p);
    }
    return { inStock: inStock.sort(byUploadedDesc), outOfStock: outOfStock.sort(byUploadedDesc) };
}

/**
 * Find out-of-stock products matching a search term by name or SKU.
 *
 * @param {Array} outOfStock - Products from partitionWeighInProducts().outOfStock.
 * @param {string} term - Search text; blank returns no results.
 * @param {Set<number>} excludeIds - Product ids already added to the weigh-in.
 * @returns {Array} Up to MAX_SEARCH_RESULTS matches.
 */
export function searchOutOfStock(outOfStock, term, excludeIds = new Set()) {
    const needle = (term || "").trim().toLowerCase();
    if (!needle) return [];
    const matches = [];
    for (const p of outOfStock) {
        if (excludeIds.has(p.id)) continue;
        const haystack = `${weighInDisplayName(p)} ${p.sku || ""}`.toLowerCase();
        if (!haystack.includes(needle)) continue;
        matches.push(p);
        if (matches.length === MAX_SEARCH_RESULTS) break;
    }
    return matches;
}
